import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import QRCode from "react-qr-code";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../Other/LoadingSpinner";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";

const MfaHandler = (props) => {
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  const [enableURL, setURL] = useState(null);
  const [mfaStatus, setMFAStatus] = useState(true);
  const [loading, setLoading] = useState(true);
  const [mfaCode, setMFACode] = useState("");
  const [selectedLength, setSelectedLength] = useState("30day");
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setMFACode(e.target.value);
  };

  const handleVerifyClick = () => {
    // Send the entered MFA code to the backend for verification
    verifyMFA(mfaCode, selectedLength);
  };
  const handleLengthButtonClick = (length) => {
    setSelectedLength(length);
  };

  useEffect(() => {
    if (user) {
      checkMfaStatus(user.id);
    }
  }, [user]);

  const getURL = async () => {
    var data = JSON.stringify({
      id: user.id,
    });

    const rawResponse = await fetch("/api/profile/enableMFA", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: data,
    });
    const content = await rawResponse.json();

    setURL(content.otpAuthUrl);
  };

  const verifyMFA = async () => {
    var data = JSON.stringify({
      id: user.id,
      token: mfaCode,
      length: selectedLength,
    });

    const rawResponse = await fetch("/api/profile/verifyMFA", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: data,
    });
    const content = await rawResponse.json();

    if (content.status === 200) {
      props.setVerify(true);
    } else {
      props.setVerify(false);
    }
  };

  const checkMfaStatus = async (id) => {
    return fetch(`/api/profile/mfaStatus?id=${id}`)
      .then((response) => response.json())
      .then((responseJson) => {
        setMFAStatus(responseJson.enabled);
        props.setPrevAuth(responseJson.prevAuth);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const enableMfaQR = () => {
    getURL();
  };

  const NonAdminRedirect = () => {
    if (!role.roles?.includes("Admin") && !role.roles?.includes("IT")) {
      navigate("/");
    } else {
      return <></>;
    }
  };

  const LENGTHS = [
    { value: "1day", label: "1 Day" },
    { value: "3day", label: "3 Days" },
    { value: "7day", label: "7 Days" },
    { value: "30day", label: "30 Days" },
  ];

  const cardClass =
    "app-card mx-auto my-10 w-full max-w-md rounded-[1.75rem] border p-6 sm:p-8";

  return (
    <div className="px-4">
      {role.roles?.includes("Admin") &&
      role.roles?.includes("IT") &&
      !mfaStatus ? (
        <div className={`${cardClass} flex flex-col items-center text-center`}>
          <span className="app-icon-tile h-12 w-12 rounded-2xl bg-sky-500/15 text-sky-600 dark:text-sky-300">
            <QrCode2RoundedIcon />
          </span>
          <h1 className="mt-4 text-xl font-bold tracking-tight">
            Set up two-step verification
          </h1>
          <p className="app-muted mt-1 text-sm leading-relaxed">
            Scan the code with your authenticator app, then click Done.
          </p>

          {!enableURL && (
            <button
              type="button"
              className="app-btn app-btn-primary mt-6"
              onClick={enableMfaQR}
            >
              Enable MFA
            </button>
          )}

          {enableURL && (
            <div className="mt-6 flex flex-col items-center gap-5">
              {/* QR codes need a light, high-contrast background to scan. */}
              <div className="app-light-surface rounded-2xl bg-white p-4 shadow-sm">
                <QRCode value={enableURL} />
              </div>
              <button
                type="button"
                className="app-btn app-btn-primary"
                onClick={() => {
                  setMFAStatus(true);
                }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      ) : (
        <NonAdminRedirect />
      )}

      {!props.mfaVerify && mfaStatus && !props.prevAuth && !loading ? (
        <div className={cardClass}>
          <span className="app-icon-tile h-12 w-12 rounded-2xl bg-sky-500/15 text-sky-600 dark:text-sky-300">
            <ShieldRoundedIcon />
          </span>
          <h1 className="mt-4 text-xl font-bold tracking-tight">
            Two-step verification
          </h1>
          <p className="app-muted mt-1 text-sm leading-relaxed">
            This page needs an extra check. Enter the 6-digit code from your
            authenticator app.
          </p>

          <label
            className="mt-6 block text-sm font-semibold"
            htmlFor="mfaCode"
          >
            Enter MFA Code
          </label>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              id="mfaCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="app-input h-12 min-w-0 flex-1 rounded-xl border px-4 text-center text-lg font-semibold tracking-[0.35em] tabular-nums"
              placeholder="123456"
              value={mfaCode}
              onChange={handleInputChange}
            />
            <button
              type="button"
              className="app-btn app-btn-primary h-12 px-6"
              onClick={handleVerifyClick}
            >
              Verify
            </button>
          </div>
          {/* Buttons for verification length */}
          <div className="mt-6">
            <span className="block text-sm font-semibold">
              Verification Length:
            </span>
            <div
              role="radiogroup"
              aria-label="Verification Length"
              className="mt-2 grid grid-cols-4 gap-1 rounded-xl bg-[color:var(--app-hover)] p-1"
            >
              {LENGTHS.map(({ value, label }) => {
                const active = selectedLength === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => handleLengthButtonClick(value)}
                    className={`rounded-lg px-2 py-2 text-sm font-semibold transition ${
                      active
                        ? "bg-white text-slate-900 shadow-sm dark:bg-white/15 dark:text-white"
                        : "app-muted hover:text-[color:var(--app-text)]"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="app-muted mt-2 text-xs">
              How long until you're asked for a code again.
            </p>
          </div>
        </div>
      ) : (
        loading && <LoadingSpinner />
      )}
    </div>
  );
};

export default MfaHandler;
